import {
  type ContainerWidthRule,
  type GapKind,
  type RoundingMode,
  type XPreviewProfile
} from './x-layout-profile'

export interface XStackImageInput {
  width: number
  height: number
}

export interface XStackItem {
  effectiveAspectRatio: number
  displayHeight: number
  y: number
  isMaxClamped: boolean
  isMinClamped: boolean
}

export interface XStackLayout {
  containerWidth: number
  usedGap: number
  rounding: RoundingMode
  items: XStackItem[]
  totalHeight: number
  compositeRatio: { width: number; height: number }
  maxAspectRatio: number
  minAspectRatio?: number
}

export interface ComputeXStackLayoutOptions {
  gapKind?: GapKind
  viewportWidth?: number
}

const resolveContainerWidth = (rule: ContainerWidthRule, viewportWidth?: number): number =>
  rule.kind === 'fixed'
    ? rule.width
    : Math.round(Math.min(rule.max, Math.max(rule.min, (viewportWidth ?? rule.max) - rule.padding * 2)))

const applyRounding = (value: number, rounding: RoundingMode): number =>
  rounding === 'floor' ? Math.floor(value) : rounding === 'ceil' ? Math.ceil(value) : Math.round(value)

const clampAspectRatio = (ratio: number, minAspectRatio: number | undefined, maxAspectRatio: number): {
  clamped: number
  isMaxClamped: boolean
  isMinClamped: boolean
} => {
  const hasMin = typeof minAspectRatio === 'number'
  const lowerBound = hasMin ? minAspectRatio : ratio
  const clampedLower = ratio < lowerBound ? lowerBound : ratio
  const clampedValue = clampedLower > maxAspectRatio ? maxAspectRatio : clampedLower
  const maxFlag = ratio > maxAspectRatio
  const minFlag = hasMin ? ratio < lowerBound : false

  return { clamped: clampedValue, isMaxClamped: maxFlag, isMinClamped: minFlag }
}

export const computeXStackLayout = (
  images: XStackImageInput[],
  profile: XPreviewProfile,
  options?: ComputeXStackLayoutOptions
): XStackLayout => {
  const containerWidth = resolveContainerWidth(profile.containerWidthRule, options?.viewportWidth)
  const gapKind = options?.gapKind ?? profile.defaultGapKind
  const usedGap = profile.gapCssPx[gapKind]
  const rounding = profile.rounding
  const items: XStackItem[] = []
  const clampResults = images.map((image) =>
    clampAspectRatio(image.width / image.height, profile.minAspectRatio, profile.maxAspectRatio)
  )
  let currentY = 0
  images.forEach((image, index) => {
    const clampResult = clampResults[index]
    const displayHeight = applyRounding(containerWidth / clampResult.clamped, rounding)
    const nextItem: XStackItem = {
      effectiveAspectRatio: clampResult.clamped,
      displayHeight,
      y: currentY,
      isMaxClamped: clampResult.isMaxClamped,
      isMinClamped: clampResult.isMinClamped
    }
    items.push(nextItem)
    const gapIncrement = index === images.length - 1 ? 0 : usedGap
    currentY = currentY + displayHeight + gapIncrement
  })
  const heightSum = items.reduce((sum, item) => sum + item.displayHeight, 0)
  const gapCount = items.length > 1 ? items.length - 1 : 0
  const totalHeight = heightSum + usedGap * gapCount

  return {
    containerWidth,
    usedGap,
    rounding,
    items,
    totalHeight,
    compositeRatio: { width: containerWidth, height: totalHeight },
    maxAspectRatio: profile.maxAspectRatio,
    minAspectRatio: profile.minAspectRatio
  }
}
